import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePericlesClient } from '../core/PericlesProvider';

import { buildEntityPathSegment } from './entityPath';

/**
 * Parameters used to configure the entity list fetcher.
 */
export interface UseEntityListParams<TFilter = unknown> {
  entity: string;
  filters?: TFilter;
  page?: number;
  pageSize?: number;
  sort?: { field: string; direction: 'asc' | 'desc' };
}

/**
 * Result returned by the list hook, including state flags and refetcher.
 */
export interface UseEntityListResult<T> {
  data: T[] | undefined;
  total: number | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Fetches a paginated list of entities using the configured Pericles client.
 */
export function useEntityList<T = unknown, TFilter = unknown>(
  params: UseEntityListParams<TFilter>
): UseEntityListResult<T> {
  const { baseUrl, fetcher } = usePericlesClient();
  const [data, setData] = useState<T[] | undefined>(undefined);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(undefined);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const queryString = useMemo(() => {
    const query = new URLSearchParams();

    query.set('page', String(params.page ?? 0));
    if (params.pageSize !== undefined) {
      query.set('pageSize', String(params.pageSize));
    }

    if (params.sort?.field) {
      query.set('sort.field', params.sort.field);
    }

    if (params.sort?.direction) {
      query.set('sort.direction', params.sort.direction);
    }

    if (params.filters !== undefined) {
      // TODO: confirm filters serialization once API DSL is finalized.
      query.set('filters', JSON.stringify(params.filters));
    }

    const serialized = query.toString();
    return serialized ? `?${serialized}` : '';
  }, [params.filters, params.page, params.pageSize, params.sort?.direction, params.sort?.field]);

  const base = useMemo(() => baseUrl.replace(/\/$/, ''), [baseUrl]);

  const url = useMemo(
    () => `${base}/entities/${buildEntityPathSegment(params.entity)}${queryString}`,
    [base, params.entity, queryString]
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      setIsError(false);
      setError(undefined);

      try {
        const response = await fetcher(url);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const json = (await response.json()) as {
          items: T[];
          meta?: { skip?: number; take?: number; total?: number };
        };

        const items = Array.isArray(json.items) ? json.items : [];
        const totalFromMeta = typeof json.meta?.total === 'number' ? json.meta.total : undefined;
        const derivedTotal = totalFromMeta ?? items.length;

        if (!isCancelled) {
          setData(items);
          setTotal(derivedTotal);
        }
      } catch (err) {
        if (!isCancelled) {
          setIsError(true);
          setError(err);
          setData(undefined);
          setTotal(undefined);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      isCancelled = true;
    };
  }, [fetcher, url, reloadToken]);

  const refetch = useCallback(() => {
    setReloadToken((previous) => previous + 1);
  }, []);

  return {
    data,
    total,
    isLoading,
    isError,
    error,
    refetch,
  };
}
